import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import IshaChat from '../components/IshaChat';
import ProfilePicCropper from '../components/ProfilePicCropper';

export default function ProfilePage() {
  const { user, token, api, updateUser } = useAuth();
  const { addNotification } = useNotifications();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    org_name: '',
    title: '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const [cropFile, setCropFile] = useState(null);

  useEffect(() => {
    if (user) {
      setForm({
        first_name: user.first_name || '',
        middle_name: user.middle_name || '',
        last_name: user.last_name || '',
        org_name: user.org_name || '',
        title: user.title || '',
      });
    }
  }, [user]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      const res = await api().put('/auth/profile', form);
      updateUser(res.data.user);
      setMsg('Profile updated successfully.');
      addNotification({
        type: 'profile',
        title: 'Profile Updated',
        message: 'Your profile information was saved successfully.',
      });
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCropFile(file);
    // Reset so the same file can be re-selected
    e.target.value = '';
  };

  const handleCrop = async (blob) => {
    setCropFile(null);
    setUploading(true);
    try {
      const picRes = await api().post('/auth/profile-pic', {});
      const axios = (await import('axios')).default;
      await axios.put(picRes.data.upload_url, blob, {
        headers: { 'Content-Type': 'image/jpeg' },
      });
      updateUser({ ...user, profile_pic_url: picRes.data.pic_url });
      setMsg('Profile picture updated.');
    } catch {
      setMsg('Failed to upload picture.');
    } finally {
      setUploading(false);
    }
  };

  const displayName = user ? `${user.first_name} ${user.last_name}` : '—';
  const memberSince = user?.created_at ? new Date(user.created_at).getFullYear() : '—';
  const picUrl = user?.profile_pic_url || (process.env.PUBLIC_URL + '/img/cancepro-logo.png');

  return (
    <>
      <h1 className="h3 mb-3"><strong>Profile</strong></h1>

      {msg && (
        <div className={`alert ${msg.includes('success') || msg.includes('updated') ? 'alert-success' : 'alert-danger'} py-2`}>
          {msg}
        </div>
      )}

      <div className="row">
        <div className="col-md-4">
          <div className="card mb-3">
            <div className="card-header">
              <h5 className="card-title mb-0">Profile Picture</h5>
            </div>
            <div className="card-body text-center">
              <img
                src={picUrl}
                alt="User"
                className="rounded-circle mb-2"
                width="128"
                height="128"
                style={{ objectFit: 'cover' }}
              />
              <h5 className="card-title mb-0">{displayName}</h5>
              <div className="text-muted mb-2">{user?.title || '—'}</div>
              <input ref={fileRef} type="file" accept="image/*" className="d-none" onChange={handleFileSelect} />
              <button
                className="btn btn-primary btn-sm"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
              >
                {uploading ? 'Uploading...' : 'Upload Photo'}
              </button>
            </div>
          </div>

          {cropFile && (
            <ProfilePicCropper
              file={cropFile}
              onCrop={handleCrop}
              onCancel={() => setCropFile(null)}
            />
          )}

          <div className="card mb-3">
            <div className="card-header">
              <h5 className="card-title mb-0">About</h5>
            </div>
            <div className="card-body">
              <ul className="list-unstyled mb-0">
                <li className="mb-1"><strong>Title:</strong> {user?.title || '—'}</li>
                <li className="mb-1"><strong>Organisation:</strong> {user?.org_name || '—'}</li>
                <li className="mb-1"><strong>User ID:</strong> {user?.user_id || '—'}</li>
                <li className="mb-1"><strong>Member Since:</strong> {memberSince}</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="col-md-8">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Account Details</h5>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-4">
                  <label className="form-label">First Name</label>
                  <input type="text" className="form-control" value={form.first_name} onChange={set('first_name')} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Middle Name</label>
                  <input type="text" className="form-control" value={form.middle_name} onChange={set('middle_name')} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Last Name</label>
                  <input type="text" className="form-control" value={form.last_name} onChange={set('last_name')} />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Email</label>
                <input type="email" className="form-control" value={user?.email || ''} readOnly />
                <small className="text-muted">Email cannot be changed.</small>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Organisation</label>
                  <input type="text" className="form-control" value={form.org_name} onChange={set('org_name')} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Title</label>
                  <input type="text" className="form-control" value={form.title} onChange={set('title')} />
                </div>
              </div>
              <button type="button" className="btn btn-primary" disabled={saving} onClick={handleSave}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <IshaChat />
    </>
  );
}
